import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
// The design's own wordmark (NeuraDesigns/assets/tjommie-logo.svg): white, and its
// artwork fills the viewBox edge-to-edge. Global/images/tjommie.svg is the same drawing
// but amber and padded into a canvas 2.8x taller than the glyphs, so at height:24px it
// rendered ~8.5px tall. Left in place because LoginPage needs the amber one.
import { ReactComponent as TjommieLogo } from "../../../Global/images/tjommie-wordmark.svg";
import { getUserName } from "../../Authentication/authSlice";
import { useGetFieldAdminAlertsQuery } from "../fieldAdminApi";
import { BG, BORDER, AMBER } from "../../../Global/colors";
import { FONT_FAMILY, initialsOf } from "../designTokens";

export default function FieldAdminTopBar({ crumb, backTo, hideBack }) {
  const navigate = useNavigate();
  const userName = useSelector(getUserName);
  // The bell was a decorative Box on every Field Admin screen. Its destination is
  // linked from 28 of the design's 29 pages; the badge is an open-alert count, since
  // the feed is derived and carries no per-alert read state.
  const { data: alertsData } = useGetFieldAdminAlertsQuery();
  // Unread, not open: the badge is a "something new" signal, so an alert you have already
  // seen but not yet actioned stays on the page without nagging from the top bar.
  const alertCount = alertsData?.unread_count || 0;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 90,
        height: "59px",
        backgroundColor: BG,
        borderBottom: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "20px",
        fontFamily: FONT_FAMILY,
      }}
    >
      {hideBack ? null : (
        <Box
          onClick={() => navigate(backTo || "/field-admin/")}
          sx={{
            width: "30px",
            height: "30px",
            borderRadius: "8px",
            backgroundColor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.65)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: "0.85rem" }} />
        </Box>
      )}

      <Box sx={{ height: "24px", display: "flex", alignItems: "center", flexShrink: 0 }}>
        <TjommieLogo style={{ height: "24px", width: "auto" }} />
      </Box>

      <Typography sx={{ color: "rgba(255,255,255,0.15)", flexShrink: 0 }}>/</Typography>

      <Typography
        sx={{
          fontSize: "0.9rem",
          fontWeight: 500,
          color: "rgba(255,255,255,0.85)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <Box component="span" sx={{ color: crumb ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.85)" }}>Field Admin{crumb ? " · " : ""}</Box>
        {crumb}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Box
        component="button"
        type="button"
        aria-label={alertCount ? `Alerts, ${alertCount} open` : "Alerts"}
        onClick={() => navigate("/field-admin/alerts/")}
        sx={{
          position: "relative",
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          backgroundColor: "rgba(253,185,64,0.12)",
          border: "1px solid rgba(253,185,64,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: AMBER,
          flexShrink: 0,
          cursor: "pointer",
          p: 0,
          "&:hover": { backgroundColor: "rgba(253,185,64,0.2)" },
        }}
      >
        <NotificationsNoneIcon sx={{ fontSize: "0.9rem" }} />
        {alertCount ? (
          <Box
            component="span"
            sx={{
              position: "absolute", top: "-3px", right: "-3px", minWidth: "14px", height: "14px",
              borderRadius: "999px", backgroundColor: "#EF4444", color: "#fff",
              border: `1.5px solid ${BG}`, fontSize: "0.56rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", px: "3px",
            }}
          >
            {alertCount}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          background: "linear-gradient(135deg,#FDB940,#E0A020)",
          color: BG,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 800,
          letterSpacing: "-0.02em",
          flexShrink: 0,
        }}
      >
        {initialsOf(userName)}
      </Box>
    </Box>
  );
}
